import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  MapPin, Heart, Download, Building2, Phone, MessageCircle, User, Share2,
  BedDouble, Bath, Layers, Volume2, VolumeX, Play, BadgeCheck,
} from "lucide-react";
import { formatPrice, formatArea, propertyTxnLabel, waLink, resolvePropertyContact } from "@/lib/format";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { isFavorite, toggleFavorite } from "@/lib/favorites";
import api from "@/lib/api";

// Small helper: renders as a <Link> when we have a profile URL, otherwise a
// plain <div> so the whole header still lays out identically on cards where
// the broker id isn't available (e.g. legacy listings).
function ProfileLink({ to, className, children, testid }) {
  if (to) {
    return <Link to={to} className={className} data-testid={testid}>{children}</Link>;
  }
  return <div className={className} data-testid={testid}>{children}</div>;
}

// Poster generator is heavy (canvas + fonts) — load it only when the user
// actually taps the Download icon on the card.
const loadPromoPoster = () => import("@/lib/promoPoster");

const PLACEHOLDER = "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=70";

/**
 * Instagram-style property card: single vertical card with a swipeable
 * photos+video carousel at top and the essential meta + actions below.
 * All heavy assets (poster generator, video playback) are opt-in.
 */
export default function PropertyCard({ property }) {
  const [saved, setSaved] = useState(() => isFavorite(property.id));
  const [cardBusy, setCardBusy] = useState(false);

  useEffect(() => {
    const sync = () => setSaved(isFavorite(property.id));
    window.addEventListener("favorites:changed", sync);
    return () => window.removeEventListener("favorites:changed", sync);
  }, [property.id]);

  const onHeart = (e) => {
    e.preventDefault(); e.stopPropagation();
    setSaved(toggleFavorite(property.id));
  };

  const onDownloadCard = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (cardBusy) return;
    setCardBusy(true);
    try {
      const { downloadPromoPoster } = await loadPromoPoster();
      await downloadPromoPoster(property, property.broker || {});
      toast.success("Property card downloaded");
    } catch {
      toast.error("Could not generate the card. Please retry.");
    } finally { setCardBusy(false); }
  };

  const onShare = async (e) => {
    e.preventDefault(); e.stopPropagation();
    const url = `${window.location.origin}/properties/${property.id}`;
    const text = `${property.title} · ${property.property_ref} on AcresDeal`;
    if (navigator.share) {
      try { await navigator.share({ title: property.title, text, url }); return; } catch {}
    }
    try {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      toast.success("Link copied — paste anywhere to share");
    } catch {
      window.open(waLink("", `${text}\n${url}`), "_blank", "noreferrer");
    }
  };

  const apartmentName = property?.extras?.apartment_name;
  const floor = property?.extras?.floor;
  // Property-specific contact (see resolvePropertyContact for the rule).
  // When `listed_by` is set, admin's own contact is intentionally suppressed.
  const contact = resolvePropertyContact(property);
  const listedName = contact.name;
  const listedPhone = contact.phone;
  const listedWhatsapp = contact.whatsapp;
  const waMsg = `Hi ${listedName || ""}, I saw ${property.property_ref} - ${property.title} on AcresDeal. Is it available?`;
  const profileHref = contact.profileHref;

  const logEnquiry = (action) => {
    api.post("/public/enquiries", { property_id: property.id, name: "Card action", phone: "0", action_type: action, source: action }).catch(() => {});
  };

  // Assemble the media items: photos first, then any videos.
  const photos = Array.isArray(property.photos) && property.photos.length ? property.photos : [PLACEHOLDER];
  const videos = Array.isArray(property.videos) ? property.videos : [];
  const media = [
    ...photos.map((src) => ({ kind: "photo", src })),
    ...videos.map((src) => ({ kind: "video", src })),
  ];

  return (
    <article
      data-testid={`property-card-${property.property_ref}`}
      className="bg-white rounded-2xl overflow-hidden border border-border shadow-sm hover:shadow-md transition-shadow"
    >
      {/* HEADER — Listed by (tappable → profile) + verified + property ref */}
      <header className="flex items-center gap-2 px-3 py-2.5 sm:gap-2.5 sm:px-4">
        <ProfileLink to={profileHref}
          className="w-9 h-9 sm:w-10 sm:h-10 rounded-full mpn-gradient-hero grid place-items-center text-white font-semibold shrink-0 overflow-hidden"
          testid={`card-avatar-${property.property_ref}`}>
          {property.broker?.agency_logo ? (
            <img src={property.broker.agency_logo} alt="" className="w-full h-full object-cover" />
          ) : (
            <span>{(listedName || "?").slice(0, 1).toUpperCase()}</span>
          )}
        </ProfileLink>

        <ProfileLink to={profileHref} className="flex-1 min-w-0 group"
                     testid={`card-listedby-link-${property.property_ref}`}>
          <div className="flex items-center gap-1 min-w-0">
            <span data-testid={`card-listed-by-${property.property_ref}`}
                  className="text-[13px] sm:text-sm font-semibold uppercase tracking-wide truncate group-hover:text-primary">
              {listedName}
            </span>
            {property.is_verified ? (
              <BadgeCheck
                data-testid={`verified-${property.property_ref}`}
                className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-primary shrink-0"
                aria-label="Verified"
              />
            ) : null}
          </div>
          <div className="text-[11px] sm:text-xs text-muted-foreground truncate">
            {(() => {
              // Account type comes from the property's poster_type (per-listing
              // override) with the broker's account default as a fallback.
              const ptype = property.poster_type || property.broker?.poster_type || "";
              const typeLabel = ptype === "broker" ? "Agent / Broker"
                             : ptype === "owner"  ? "Owner"
                             : (property.broker?.agency ? "Agency" : "Direct seller");
              return contact.hasOverride ? `${typeLabel} · Listed via AcresDeal` : typeLabel;
            })()}
          </div>
        </ProfileLink>

        <span className="mpn-chip text-[10px] sm:text-[11px] font-semibold bg-muted shrink-0"
              data-testid={`card-ref-${property.property_ref}`}>
          {property.property_ref}
        </span>
      </header>

      {/* MEDIA — swipeable photos + video, tap opens details */}
      <MediaCarousel
        media={media}
        propertyRef={property.property_ref}
        detailHref={`/properties/${property.id}`}
        detailState={{ property }}
      />

      {/* ACTION ROW — Instagram-style icon strip */}
      <div className="flex items-center gap-1 px-2 sm:px-3 py-2">
        <IconAction
          testid={`heart-${property.property_ref}`}
          onClick={onHeart}
          label={saved ? "Remove from saved" : "Save"}
          active={saved} activeClass="text-rose-500">
          <Heart className={`w-5 h-5 ${saved ? "fill-current" : ""}`} />
        </IconAction>
        <IconAction
          testid={`share-card-${property.property_ref}`}
          onClick={onShare}
          label="Share this property">
          <Share2 className="w-5 h-5" />
        </IconAction>
        <IconAction
          testid={`download-card-${property.property_ref}`}
          onClick={onDownloadCard}
          disabled={cardBusy}
          label="Download property poster">
          <Download className="w-5 h-5" />
        </IconAction>
        <span className="flex-1" />
        <Link
          to={`/properties/${property.id}`}
          state={{ property }}
          data-testid={`view-full-${property.property_ref}`}
          className="text-xs sm:text-sm font-semibold text-primary hover:underline whitespace-nowrap"
        >
          View full →
        </Link>
      </div>

      {/* META */}
      <div className="px-3 sm:px-4 pb-2.5">
        <Link
          to={`/properties/${property.id}`}
          state={{ property }}
          className="block group"
        >
          <h3 className="font-display font-bold text-[14px] sm:text-base leading-snug uppercase tracking-wide group-hover:text-primary line-clamp-2">
            {property.title}
          </h3>
        </Link>
        {apartmentName ? (
          <div className="mt-1 flex items-center gap-1 text-[11px] sm:text-xs text-primary font-semibold uppercase tracking-wide"
               data-testid={`apartment-name-${property.property_ref}`}>
            <Building2 className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" />
            <span className="truncate">{apartmentName}</span>
          </div>
        ) : null}
        <div className="mt-1 flex items-center gap-1 text-[11px] sm:text-xs text-muted-foreground">
          <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" />
          <span className="truncate">{property.locality ? `${property.locality} · ` : ""}{property.location}</span>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] sm:text-sm">
          <span className="font-bold text-primary" data-testid={`card-price-${property.property_ref}`}>
            {formatPrice(property.price, property.transaction_type)}
          </span>
          <span className="text-muted-foreground">·</span>
          <span className="font-semibold">{formatArea(property.area, property.area_unit)}</span>
          {property.bedrooms > 0 ? (
            <>
              <span className="text-muted-foreground">·</span>
              <span className="inline-flex items-center gap-1"><BedDouble className="w-3.5 h-3.5" /> {property.bedrooms}</span>
            </>
          ) : null}
          {property.bathrooms > 0 ? (
            <>
              <span className="text-muted-foreground">·</span>
              <span className="inline-flex items-center gap-1"><Bath className="w-3.5 h-3.5" /> {property.bathrooms}</span>
            </>
          ) : null}
          {floor ? (
            <>
              <span className="text-muted-foreground">·</span>
              <span className="inline-flex items-center gap-1"><Layers className="w-3.5 h-3.5" /> {floor}</span>
            </>
          ) : null}
        </div>
      </div>

      {/* CONTACT — compact Call + WhatsApp */}
      {listedPhone ? (
        <div className="px-3 sm:px-4 pb-3 flex items-center gap-2">
          <a href={`tel:${listedPhone}`}
             onClick={(e) => { e.stopPropagation(); logEnquiry("call"); }}
             data-testid={`card-call-${property.property_ref}`}
             className="flex-1 h-10 rounded-full bg-primary text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5">
            <Phone className="w-4 h-4" /> Call
          </a>
          <a href={waLink(listedWhatsapp, waMsg)} target="_blank" rel="noreferrer"
             onClick={(e) => { e.stopPropagation(); logEnquiry("whatsapp"); }}
             data-testid={`card-whatsapp-${property.property_ref}`}
             className="flex-1 h-10 rounded-full border border-emerald-500 text-emerald-600 hover:bg-emerald-50 text-sm font-semibold inline-flex items-center justify-center gap-1.5">
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </a>
        </div>
      ) : (
        <div className="px-3 sm:px-4 pb-3">
          <Link to={`/properties/${property.id}`} state={{ property }}
                className="block text-center text-xs text-muted-foreground italic">
            Contact via property page
          </Link>
        </div>
      )}
    </article>
  );
}

/* ---------- Sub-components ---------- */

function IconAction({ children, testid, onClick, label, active, activeClass = "", disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      title={label}
      aria-label={label}
      aria-pressed={active || undefined}
      className={`w-9 h-9 grid place-items-center rounded-full hover:bg-muted transition-colors disabled:opacity-50 ${active ? activeClass : "text-foreground"}`}
    >
      {children}
    </button>
  );
}

/**
 * Horizontal swipe carousel using CSS scroll-snap. IntersectionObserver
 * inside the scroller keeps the "1/N" indicator accurate without any
 * external dependency. Videos are muted and only play on tap.
 */
function MediaCarousel({ media, propertyRef, detailHref, detailState }) {
  const scrollerRef = useRef(null);
  const [idx, setIdx] = useState(0);
  const total = media.length;

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const slides = Array.from(scroller.querySelectorAll("[data-slide]"));
    if (!slides.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        // Pick the entry that is most visible in the horizontal scroller.
        let best = null;
        for (const e of entries) {
          if (!best || e.intersectionRatio > best.intersectionRatio) best = e;
        }
        if (best && best.isIntersecting) {
          const i = Number(best.target.getAttribute("data-slide"));
          if (!Number.isNaN(i)) setIdx(i);
        }
      },
      { root: scroller, threshold: [0.55, 0.85] },
    );
    slides.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, [total]);

  const scrollTo = useCallback((i) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const slide = scroller.querySelector(`[data-slide="${i}"]`);
    if (slide) slide.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
  }, []);

  return (
    <div className="relative bg-muted">
      <div
        ref={scrollerRef}
        data-testid={`media-carousel-${propertyRef}`}
        className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar"
        style={{ scrollbarWidth: "none" }}
      >
        {media.map((m, i) => (
          <div
            key={`${m.kind}-${i}`}
            data-slide={i}
            className="shrink-0 w-full snap-start relative aspect-[4/5] sm:aspect-[16/11]"
          >
            {m.kind === "photo" ? (
              <Link
                to={detailHref}
                state={detailState}
                className="block w-full h-full"
                data-testid={`media-photo-${propertyRef}-${i}`}
              >
                <img
                  src={m.src}
                  alt=""
                  loading={i === 0 ? "eager" : "lazy"}
                  decoding="async"
                  fetchpriority={i === 0 ? "high" : undefined}
                  className="w-full h-full object-cover"
                />
              </Link>
            ) : (
              <TapToPlayVideo src={m.src} idx={i} propertyRef={propertyRef} />
            )}
          </div>
        ))}
      </div>

      {/* 1/N indicator (only when there are multiple items) */}
      {total > 1 ? (
        <div className="pointer-events-none absolute top-2 right-2 mpn-chip text-[10px] sm:text-xs bg-black/60 text-white border border-white/20 backdrop-blur"
             data-testid={`media-count-${propertyRef}`}>
          {idx + 1} / {total}
        </div>
      ) : null}

      {/* Dot pagination — helpful on desktop where swipe is a drag */}
      {total > 1 ? (
        <div className="pointer-events-auto absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
          {media.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); scrollTo(i); }}
              aria-label={`Go to slide ${i + 1}`}
              className={`w-1.5 h-1.5 rounded-full transition-all ${i === idx ? "w-4 bg-white" : "bg-white/60"}`}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function TapToPlayVideo({ src, idx, propertyRef }) {
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);
  const toggle = (e) => {
    e.preventDefault(); e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play().catch(() => {}); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };
  const toggleMute = (e) => {
    e.preventDefault(); e.stopPropagation();
    setMuted((m) => {
      const next = !m;
      if (videoRef.current) videoRef.current.muted = next;
      return next;
    });
  };
  return (
    <div className="relative w-full h-full bg-black">
      <video
        ref={videoRef}
        src={src}
        muted
        playsInline
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onClick={toggle}
        data-testid={`media-video-${propertyRef}-${idx}`}
        className="w-full h-full object-cover"
      />
      {!playing ? (
        <button
          type="button"
          onClick={toggle}
          aria-label="Play video"
          className="absolute inset-0 grid place-items-center bg-black/20 hover:bg-black/30 transition-colors"
        >
          <span className="w-14 h-14 rounded-full bg-white/90 grid place-items-center">
            <Play className="w-6 h-6 text-primary ml-0.5" />
          </span>
        </button>
      ) : null}
      <button
        type="button"
        onClick={toggleMute}
        aria-label={muted ? "Unmute" : "Mute"}
        className="absolute bottom-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white grid place-items-center border border-white/20"
      >
        {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
      </button>
    </div>
  );
}
